import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import {
  AssignedProperty,
  AssignedPropertyValue,
  Assignee,
  DefaultType,
  Property,
  PropertyClient,
  PropertyValue,
} from 'api/clients/PropertyClient'
import { handleErrorResponseWithMessage } from 'api/utils/handleErrorResponseWithMessage'
import useIsMounted from 'ismounted'
import { type EditorView } from 'prosemirror-view'
import { useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { deleteFilesProperties, upsertFilesProperties } from 'redux/actions/filesProperties'
import { buildErrorMessage } from 'utils'
import { wordCountOfDoc } from 'utils/wordCountOfDoc'

interface Props {
  assignee: Assignee
  view?: EditorView | undefined
  propertyId?: number
}

export const useAssignedProperties = ({ assignee, view, propertyId }: Props) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [assignedProperties, setAssignedProperties] = useState<AssignedProperty[]>([])

  const docInfoAssignedProperties = assignedProperties.filter(
    (ap: AssignedProperty) => !ap.property.scope_project_id && !!ap.property.default_type,
  )
  const fileAssignedProperties = assignedProperties.filter((ap: AssignedProperty) => !ap.property.default_type)

  const assignedPropertiesClient = useRef(
    PropertyClient.getAssignedProperties<AssignedProperty>(assignee.id, assignee.type, propertyId),
  )
  const isMounted = useIsMounted()

  const createAssignedProperty = useCallback(
    async (property: Property) => {
      const response = await PropertyClient.createAssignedProperty<AssignedProperty>(
        assignee.id,
        property,
        assignee.type,
      )
      handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)

      if (response && response.type === SUCCESS_RESPONSE_TYPE) {
        dispatch(upsertFilesProperties([response.data.assigned_property]))
      }

      return response.data.assigned_property
    },
    [assignee, dispatch],
  )

  const addPropertyToAssignee = useCallback(
    async (property: Property) => {
      isMounted.current && setLoading(true)
      const assignedProperty = await createAssignedProperty(property)
      isMounted.current &&
        assignedProperty &&
        setAssignedProperties((oldProperties) => [...oldProperties, assignedProperty])

      isMounted.current && setLoading(false)
    },
    [isMounted, createAssignedProperty],
  )

  const removePropertyFromAssignee = useCallback(
    async (id: number | undefined) => {
      isMounted.current && setLoading(true)
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const response = await PropertyClient.bulkDeleteAssignedProperties([assignee], id!)
      handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)
      if (response && response.type === SUCCESS_RESPONSE_TYPE) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        dispatch(deleteFilesProperties([assignee], id!))
        isMounted.current &&
          setAssignedProperties((oldProperties) => {
            return oldProperties.filter((assignedProperty) => assignedProperty.property.id !== id)
          })
      }

      isMounted.current && setLoading(false)
    },
    [dispatch, assignee, isMounted],
  )

  const addPropertyValueToAssignedProperties = useCallback(
    async (assignedPropertyId: number | undefined, propertyValue: PropertyValue) => {
      const newAssignedProperty =
        !assignedPropertyId && (await createAssignedProperty({ id: propertyValue.property_id } as Property))

      const correctAssignedPropertyId = newAssignedProperty ? newAssignedProperty.id : assignedPropertyId
      const response = await PropertyClient.createAssignedPropertyValue(
        correctAssignedPropertyId as number,
        propertyValue,
      )
      const {
        data: { property_value },
      } = response

      const assignedProperty = newAssignedProperty || assignedProperties.find((ap) => ap.id === assignedPropertyId)

      if (assignedProperty) {
        const newAssignedPropertyWithAssignedValue = {
          ...assignedProperty,
          property_values: [property_value],
        }

        dispatch(upsertFilesProperties([newAssignedPropertyWithAssignedValue]))
      }

      isMounted.current &&
        property_value &&
        setAssignedProperties((oldProperties) =>
          [...oldProperties, newAssignedProperty as AssignedProperty].filter(Boolean).map((assignedProperty) => {
            if (assignedProperty.property.id !== property_value.property_id) return assignedProperty

            return {
              ...assignedProperty,
              property: {
                ...assignedProperty.property,
                property_values: [...assignedProperty.property.property_values, property_value],
              },
              property_values: [property_value],
            }
          }),
        )
    },
    [isMounted, createAssignedProperty, dispatch, assignedProperties],
  )

  const updateAssignedProperty = useCallback(
    async (
      assignedPropertyId: number | undefined,
      property: Property,
      assignedPropertyValues: AssignedPropertyValue[],
    ) => {
      isMounted.current && setLoading(true)

      const newAssignedProperty = !assignedPropertyId && (await createAssignedProperty(property))
      const correctAssignedPropertyId = newAssignedProperty ? newAssignedProperty.id : assignedPropertyId

      const response = await PropertyClient.updateAssignedProperty<AssignedProperty>(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        correctAssignedPropertyId!,
        property,
        assignedPropertyValues,
      )
      handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)

      if (response && response.type === SUCCESS_RESPONSE_TYPE) {
        const {
          data: {
            assigned_property: { property, property_values },
          },
        } = response

        dispatch(upsertFilesProperties([response.data.assigned_property]))
        isMounted.current &&
          setAssignedProperties((oldProperties) =>
            [...oldProperties, newAssignedProperty as AssignedProperty].filter(Boolean).map((assignedProperty) => {
              if (assignedProperty.property.id !== property.id) return assignedProperty

              return {
                ...assignedProperty,
                property_values: property_values,
              }
            }),
          )
      }

      isMounted.current && setLoading(false)
    },
    [dispatch, isMounted, createAssignedProperty],
  )

  const refreshAssignedProperty = useCallback(
    async (property: Property) => {
      const selectedAssignedProperty = assignedProperties.find((ap) => ap.property.id === property.id)
      if (!selectedAssignedProperty?.id) return

      const response = await PropertyClient.getAssignedProperty<AssignedProperty>(selectedAssignedProperty.id)

      if (response && response.type === SUCCESS_RESPONSE_TYPE) {
        const {
          data: { assigned_property: articleProperty },
        } = response

        dispatch(upsertFilesProperties([response.data.assigned_property]))
        isMounted.current &&
          setAssignedProperties((oldProperties) =>
            oldProperties.map((assignedProperty) => {
              if (assignedProperty.id !== articleProperty.id) return assignedProperty

              return {
                ...articleProperty,
                property_values: articleProperty.property_values,
              }
            }),
          )
      }
    },
    [assignedProperties, dispatch, isMounted],
  )

  const updateProperty = useCallback(
    (property: Property) => {
      refreshAssignedProperty(property)

      setAssignedProperties((oldProperties) =>
        oldProperties.map((assignedProperty) => {
          if (assignedProperty.property.id !== property.id) return assignedProperty

          return {
            ...assignedProperty,
            property: property,
          }
        }),
      )
    },
    [refreshAssignedProperty],
  )

  const updateWordCountProperty = useCallback(
    (assignedProperties: AssignedProperty[], view: EditorView | undefined) => {
      if (!view) {
        setAssignedProperties(
          assignedProperties.filter(({ property: { default_type } }) => default_type !== DefaultType.WordCount),
        )
        return
      }
      const wordCountAssignedProperty = assignedProperties.find(
        (assignedProperty) => assignedProperty.property.default_type === DefaultType.WordCount,
      )
      const currentWordCount = wordCountAssignedProperty?.property_values[0].value

      const newWordCount = wordCountOfDoc(view.state.doc)

      if (newWordCount !== currentWordCount) {
        setAssignedProperties(
          assignedProperties.map((assignedProperty) => {
            if (
              wordCountAssignedProperty &&
              assignedProperty.property.default_type === DefaultType.WordCount &&
              wordCountAssignedProperty.property_values.length
            ) {
              return {
                ...wordCountAssignedProperty,
                property_values: [
                  {
                    ...wordCountAssignedProperty.property_values[0],
                    value: newWordCount,
                  },
                ],
              }
            }

            return assignedProperty
          }),
        )
      }
    },
    [],
  )

  const loadMore = useCallback(async () => {
    isMounted.current && setLoading(true)
    const response = await assignedPropertiesClient.current.loadMore()
    handleErrorResponseWithMessage(buildErrorMessage(response?.data.errors))(response)

    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      const {
        data: { assigned_properties },
      } = response

      dispatch(upsertFilesProperties(response.data.assigned_properties))
      isMounted.current && setAssignedProperties((oldProperties) => oldProperties.concat(assigned_properties))
      isMounted.current && updateWordCountProperty(assigned_properties, view)
    }

    isMounted.current && setLoading(false)
  }, [assignedPropertiesClient, updateWordCountProperty, view, dispatch, isMounted])

  return {
    assignedProperties,
    fileAssignedProperties,
    setAssignedProperties,
    hasMore: assignedPropertiesClient.current.hasMore,
    docInfoAssignedProperties,
    updateWordCountProperty,
    loading,
    loadMore,
    addPropertyToAssignee,
    updateAssignedProperty,
    updateProperty,
    removePropertyFromAssignee,
    refreshAssignedProperty,
    addPropertyValueToAssignedProperties,
    createAssignedProperty,
  }
}
